import React, { useState, useEffect } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { useSelector } from "react-redux";
import { AddTraderOffersData } from "../../../../Rest_api/DemoRestApi";
import OfferListTable from "./AddOfferTable";
import TimeInput from "../../../../components/TimeInput/TimeInput";
import { ErrorPopup } from "../../../../components/ErrorHandling";

interface TimeSlot {
  id: string;
  start_slot: string;
  end_slot: string;
  quantum: string;
  rate: string;
  label?: string;
}

interface OfferRow {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  type: "RTC" | "PEAK" | "CUSTOM";
  timeSlots: TimeSlot[];
}

interface PowerAddOfferProps {
  onOfferAdded?: () => void;
  refreshKey?: any;
  setRefreshKey?: React.Dispatch<React.SetStateAction<any>>;
}

export default function PowerAddOffer({
  onOfferAdded,
  refreshKey,
  setRefreshKey,
}: PowerAddOfferProps) {
  const SelectedStartDate = useSelector(
    (state: any) => state.ProcurementOptimizerDemoDate.startDate
  );
  const SelectedEndDate = useSelector(
    (state: any) => state.ProcurementOptimizerDemoDate.endDate
  );

  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offers, setOffers] = useState<OfferRow[]>([
    {
      id: "rtc-1",
      name: "",
      startDate: SelectedStartDate || "",
      endDate: SelectedEndDate || "",
      type: "RTC",
      timeSlots: [
        {
          id: "slot-1",
          start_slot: "00:00",
          end_slot: "24:00",
          quantum: "",
          rate: "",
        },
      ],
    },
    {
      id: "peak-1",
      name: "",
      startDate: SelectedStartDate || "",
      endDate: SelectedEndDate || "",
      type: "PEAK",
      timeSlots: [
        {
          id: "slot-1",
          start_slot: "",
          end_slot: "",
          quantum: "",
          rate: "",
          label: "Morning Peak",
        },
        {
          id: "slot-2",
          start_slot: "",
          end_slot: "",
          quantum: "",
          rate: "",
          label: "Evening Peak",
        },
      ],
    },
    {
      id: "custom-1",
      name: "",
      startDate: SelectedStartDate || "",
      endDate: SelectedEndDate || "",
      type: "CUSTOM",
      timeSlots: [
        {
          id: "slot-1",
          start_slot: "",
          end_slot: "",
          quantum: "",
          rate: "",
        },
      ],
    },
  ]);
  const [peakSelected, setPeakSelected] = useState({
    morningPeak: true,
    eveningPeak: false,
  });

  useEffect(() => {
    if (SelectedStartDate || SelectedEndDate) {
      setOffers((prev) =>
        prev.map((offer) => ({
          ...offer,
          startDate: SelectedStartDate || "",
          endDate: SelectedEndDate || "",
        })),
      );
    }
  }, [SelectedStartDate, SelectedEndDate]);

  const handleUpdateOffer = (id: string, field: keyof OfferRow, value: any) => {
    setOffers(
      offers.map((offer) =>
        offer.id === id ? { ...offer, [field]: value } : offer,
      ),
    );
  };

  const handleUpdateTimeSlot = (
    offerId: string,
    slotId: string,
    field: keyof TimeSlot,
    value: string,
  ) => {
    // Validate numeric fields
    if (field === "quantum" || field === "rate") {
      // Allow empty string or valid numbers
      if (value !== "" && (isNaN(Number(value)) || Number(value) < 0)) {
        return;
      }
      // Check max value for rate
      if (field === "rate" && value !== "" && Number(value) > 10) {
        return;
      }
    }

    setOffers(
      offers.map((offer) => {
        if (offer.id === offerId) {
          return {
            ...offer,
            timeSlots: offer.timeSlots.map((slot) =>
              slot.id === slotId ? { ...slot, [field]: value } : slot,
            ),
          };
        }
        return offer;
      }),
    );
  };

  const handleAddCustomTimeSlot = () => {
    setOffers(
      offers.map((offer) => {
        if (offer.type === "CUSTOM") {
          const lastSlot = offer.timeSlots[offer.timeSlots.length - 1];
          return {
            ...offer,
            timeSlots: [
              ...offer.timeSlots,
              {
                id: `slot-${Date.now()}`,
                start_slot: lastSlot?.end_slot || "",
                end_slot: "",
                quantum: lastSlot?.quantum || "",
                rate: lastSlot?.rate || "",
              },
            ],
          };
        }
        return offer;
      }),
    );
  };

  const handleDeleteCustomTimeSlot = (slotId: string) => {
    setOffers(
      offers.map((offer) => {
        if (offer.type === "CUSTOM" && offer.timeSlots.length > 1) {
          return {
            ...offer,
            timeSlots: offer.timeSlots.filter((slot) => slot.id !== slotId),
          };
        }
        return offer;
      }),
    );
  };

  const handleSubmit = async (offerType: "RTC" | "PEAK" | "CUSTOM") => {
    const offer = offers.find((o) => o.type === offerType);
    if (!offer) return;

    if (!offer.startDate || !offer.endDate) {
      setError("Please select Start and End Dates.");
      return;
    }

    let slotsToSend = offer.timeSlots;
    if (offerType === "PEAK") {
      slotsToSend = [];
      if (peakSelected.morningPeak) slotsToSend.push(offer.timeSlots[0]);
      if (peakSelected.eveningPeak) slotsToSend.push(offer.timeSlots[1]);

      if (slotsToSend.length === 0) {
        setError("Please select at least one peak slot (Morning or Evening).");
        return;
      }
    }

    for (const slot of slotsToSend) {
      if (!slot.quantum || !slot.rate) {
        setError("Please fill all required fields.");
        return;
      }
      if (offerType !== "RTC" && (!slot.start_slot || !slot.end_slot)) {
        setError("Please enter start and end times.");
        return;
      }
    }

    setLoading(offerType);
    try {
      const apiData = [
        {
          type: offer.type,
          name: offer.name,
          start_date: offer.startDate,
          end_date: offer.endDate,
          trader_offer: slotsToSend.map(({ id, label, ...rest }) => rest),
        },
      ];

      const res = await AddTraderOffersData(apiData);
      if (res) {
        if (setRefreshKey) setRefreshKey((prev: number) => prev + 1);
        if (onOfferAdded) onOfferAdded();
        setOffers((prev) =>
          prev.map((o) =>
            o.type === offerType
              ? {
                  ...o,
                  name: "",
                  timeSlots:
                    o.type === "CUSTOM"
                      ? [
                          {
                            id: "slot-1",
                            start_slot: "",
                            end_slot: "",
                            quantum: "",
                            rate: "",
                          },
                        ]
                      : o.timeSlots.map((s) => ({
                          ...s,
                          start_slot: o.type === "RTC" ? "00:00" : "",
                          end_slot: o.type === "RTC" ? "24:00" : "",
                          quantum: "",
                          rate: "",
                        })),
                }
              : o,
          ),
        );
        setPeakSelected({ morningPeak: true, eveningPeak: false });
      }
    } catch (error) {
      console.error(error);
      setError("Error submitting offer. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  // Helper function to determine if card should have shadow
  const shouldShowShadow = (offer: OfferRow) => {
    if (offer.type === "PEAK") {
      return peakSelected.morningPeak && peakSelected.eveningPeak;
    }
    if (offer.type === "CUSTOM") {
      return offer.timeSlots.length > 1;
    }
    return false;
  };

  return (
    <>
      <div className="bg-[#FFFFFF52] rounded-2xl shadow-sm border border-[#DFEAF2] relative h-[333px]">
        <div className="bg-white flex items-center justify-between mb-2 border-b rounded-2xl border-[#DFEAF2] p-4">
          <span className="text-[#6F6D7C] text-sm font-medium">
            Load Trader Offers
          </span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 items-start absolute z-20 px-1 pb-0">
          {offers.map((offer) => (
            <div
              key={offer.id}
              className={`bg-white mx-1 rounded-xl border border-[#DFEAF2] flex flex-col hover:z-50 relative min-h-[265px] transition-shadow duration-300 ${
                shouldShowShadow(offer) ? "shadow-2xl" : "hover:shadow-lg"
              }`}
            >
              {/* Card Header */}
              <div className="px-4 py-2 flex items-center justify-between border-b border-[#DFEAF2]">
                <h2 className="text-[#34656D] text-sm font-medium">
                  {offer.type === "RTC" ? "RTC" : offer.type}
                </h2>
                <button
                  onClick={() => handleSubmit(offer.type)}
                  disabled={loading === offer.type}
                  className="w-[82px] h-[24px] bg-[#FFFAF6] text-[#F1935C] border border-[#F1935C] rounded-lg text-xs font-medium hover:bg-[#F1935C] hover:text-white transition-colors disabled:opacity-50"
                >
                  {loading === offer.type ? "Loading..." : "Add Offer"}
                </button>
              </div>

              {/* Card Body */}
              <div className="p-4 flex-1 flex flex-col gap-5">
                {/* Row 1: Offer Name, Start Date, End Date */}
                <div className="grid lg:grid-cols-3 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-[#6F6D7C]">
                      Offer Name
                    </label>
                    <input
                      type="text"
                      placeholder="Enter Name"
                      value={offer.name}
                      onChange={(e) =>
                        handleUpdateOffer(offer.id, "name", e.target.value)
                      }
                      className="w-full px-2 py-2 border border-[#E2E2E5] rounded-lg text-xs text-[#6F6D7C] focus:ring-1 focus:ring-[#F1935C] outline-none font-regular h-[40px]"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-[#6F6D7C]">
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={offer.startDate}
                      onChange={(e) =>
                        handleUpdateOffer(offer.id, "startDate", e.target.value)
                      }
                      min={SelectedStartDate}
                      max={SelectedEndDate}
                      className="w-full px-2 py-2 border border-[#E2E2E5] rounded-lg text-xs text-[#6F6D7C] focus:ring-1 focus:ring-[#F1935C] outline-none font-regular h-[40px]"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-xs font-medium text-[#6F6D7C]">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={offer.endDate}
                      onChange={(e) =>
                        handleUpdateOffer(offer.id, "endDate", e.target.value)
                      }
                      min={offer.startDate || SelectedStartDate}
                      max={SelectedEndDate}
                      className="w-full px-2 py-2 border border-[#E2E2E5] rounded-lg text-xs text-[#6F6D7C] focus:ring-1 focus:ring-[#F1935C] outline-none font-regular h-[40px]"
                    />
                  </div>
                </div>

                {/* Peak Selection */}

                {/* Time Slots Area */}
                <div className="space-y-5 relative">
                  {offer.type === "PEAK" && (
                    <div className="flex items-center gap-2 absolute top-[-8px] right-[10px] z-[99]">
                      <label
                        className="flex items-center gap-2 cursor-pointer select-none"
                        onClick={() => {
                          if (
                            peakSelected.morningPeak &&
                            !peakSelected.eveningPeak
                          )
                            return;
                          setPeakSelected({
                            ...peakSelected,
                            morningPeak: !peakSelected.morningPeak,
                          });
                        }}
                      >
                        <div
                          className={`w-4 h-4 rounded-md border ${
                            peakSelected.morningPeak
                              ? "bg-[#F1935C] border-[#F1935C]"
                              : "border-gray-300 bg-white"
                          } flex items-center justify-center transition-colors shadow-sm`}
                        >
                          {peakSelected.morningPeak && (
                            <div className="w-1.5 h-1.5 bg-white rounded-full shadow-inner" />
                          )}
                        </div>
                        <span
                          className={`text-xs font-medium ${
                            peakSelected.morningPeak
                              ? "text-[#F1935C]"
                              : "text-[#6B7281]"
                          }`}
                        >
                          Morning Peak
                        </span>
                      </label>
                      <label
                        className="flex items-center gap-2 cursor-pointer select-none"
                        onClick={() => {
                          if (
                            !peakSelected.morningPeak &&
                            peakSelected.eveningPeak
                          )
                            return;
                          setPeakSelected({
                            ...peakSelected,
                            eveningPeak: !peakSelected.eveningPeak,
                          });
                        }}
                      >
                        <div
                          className={`w-4 h-4 rounded-md border ${
                            peakSelected.eveningPeak
                              ? "bg-[#F1935C] border-[#F1935C]"
                              : "border-gray-300 bg-white"
                          } flex items-center justify-center transition-colors shadow-sm`}
                        >
                          {peakSelected.eveningPeak && (
                            <div className="w-1.5 h-1.5 bg-white rounded-full shadow-inner" />
                          )}
                        </div>
                        <span
                          className={`text-xs font-medium ${
                            peakSelected.eveningPeak
                              ? "text-[#F1935C]"
                              : "text-[#6B7281]"
                          }`}
                        >
                          Evening Peak
                        </span>
                      </label>
                    </div>
                  )}
                  <div
                    className={`${
                      offer.type === "CUSTOM" && offer.timeSlots.length > 3
                        ? "max-h-[380px] overflow-y-auto pt-3 pr-1 custom-scroll"
                        : ""
                    }`}
                  >
                    <div className="space-y-5">
                      {offer.timeSlots
                        .filter((slot) => {
                          if (offer.type !== "PEAK") return true;
                          if (slot.label === "Morning Peak")
                            return peakSelected.morningPeak;
                          if (slot.label === "Evening Peak")
                            return peakSelected.eveningPeak;
                          return true;
                        })
                        .map((slot) => {
                          const actualSlotIndexInSource =
                            offer.timeSlots.findIndex((s) => s.id === slot.id);
                          return (
                            <div key={slot.id} className="space-y-5 relative">
                              {/* Label for Peak type */}
                              {offer.type === "PEAK" && slot.label && (
                                <div className="bg-[#FBFBFC] text-xs rounded-xl font-medium text-[#F1935C] px-1 absolute top-[-9px] left-[10px] z-[99]">
                                  {slot.label}
                                </div>
                              )}

                              <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-2 items-end bg-[#F5F5F5] p-2 rounded-xl border border-[#DFEAF2] group relative">
                                {/* Start Time */}
                                <div className="space-y-1.5">
                                  <label className="text-xs font-medium text-[#6F6D7C] tracking-wider">
                                    Start Time
                                  </label>
                                  {offer.type === "RTC" ? (
                                    <div className="w-full px-2 py-2 border border-[#E2E2E5] rounded-lg bg-[#E2E2E5] text-[#8C8A96] text-xs font-regular flex items-center h-[40px] cursor-not-allowed">
                                      00:00
                                    </div>
                                  ) : (
                                    <div
                                      className={`w-full h-[40px] px-2 py-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-gray-700 `}
                                    >
                                      <TimeInput
                                        onChange={(val) =>
                                          handleUpdateTimeSlot(
                                            offer.id,
                                            slot.id,
                                            "start_slot",
                                            val,
                                          )
                                        }
                                        height={32}
                                        type="startTime"
                                        value={slot.start_slot}
                                        heading={""}
                                      />
                                    </div>
                                  )}
                                </div>

                                {/* End Time */}
                                <div className="space-y-1.5">
                                  <label className="text-xs font-medium text-[#6F6D7C] tracking-wider">
                                    End Time
                                  </label>
                                  {offer.type === "RTC" ? (
                                    <div className="w-full px-2 py-2 border border-[#E2E2E5] rounded-lg bg-[#E2E2E5] text-[#8C8A96] text-xs font-regular flex items-center h-[40px] cursor-not-allowed">
                                      24:00
                                    </div>
                                  ) : (
                                    <div
                                      className={`w-full h-[40px] px-2 py-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-gray-700 `}
                                    >
                                      <TimeInput
                                        onChange={(val) =>
                                          handleUpdateTimeSlot(
                                            offer.id,
                                            slot.id,
                                            "end_slot",
                                            val,
                                          )
                                        }
                                        height={32}
                                        type="endTime"
                                        value={slot.end_slot}
                                        heading={""}
                                        minTime={slot.start_slot}
                                      />
                                    </div>
                                  )}
                                </div>

                                {/* Quantum */}
                                <div className="space-y-1.5">
                                  <label className="text-xs font-medium text-[#6F6D7C] tracking-wider">
                                    Quantum{" "}
                                    <span className="text-[10px]">(MW)</span>
                                  </label>
                                  <input
                                    type="number"
                                    placeholder="Value"
                                    value={slot.quantum}
                                    onChange={(e) =>
                                      handleUpdateTimeSlot(
                                        offer.id,
                                        slot.id,
                                        "quantum",
                                        e.target.value,
                                      )
                                    }
                                    min="0"
                                    step="0.01"
                                    className="w-full px-2 py-2 border border-[#E2E2E5] rounded-lg text-xs text-[#8C8A96] focus:ring-1 focus:ring-[#F1935C] outline-none font-regular h-[40px] bg-white transition-all"
                                  />
                                </div>

                                {/* Rate */}
                                <div className="space-y-1.5">
                                  <label className="text-xs font-medium text-[#6F6D7C] tracking-wider">
                                    Rate{" "}
                                    <span className="text-[10px]">(₹/kWh)</span>
                                  </label>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="number"
                                      placeholder="Value"
                                      value={slot.rate}
                                      onChange={(e) =>
                                        handleUpdateTimeSlot(
                                          offer.id,
                                          slot.id,
                                          "rate",
                                          e.target.value,
                                        )
                                      }
                                      min="0"
                                      max="10"
                                      step="0.01"
                                      className="w-full px-2 py-2 border border-[#E2E2E5] rounded-lg text-xs text-[#8C8A96] focus:ring-1 focus:ring-[#F1935C] outline-none font-regular h-[40px] bg-white transition-all"
                                    />
                                    {offer.type === "CUSTOM" && (
                                      <div className="flex gap-1.5 shrink-0">
                                        <button
                                          onClick={handleAddCustomTimeSlot}
                                          className="p-1 rounded-lg border border-[#F1935C] bg-[#FFFAF6] text-[#FF8C42] hover:bg-[#F1935C] hover:text-white transition-all shadow-sm absolute right-[0px] top-[-12px]"
                                          title="Add Slot"
                                        >
                                          <Plus size={13} strokeWidth={3} />
                                        </button>
                                        {offer.timeSlots.length > 1 && (
                                          <button
                                            onClick={() =>
                                              handleDeleteCustomTimeSlot(
                                                slot.id,
                                              )
                                            }
                                            className="p-1 rounded-lg border border-[#EC1C24] bg-[#FFF1EF] text-[#EC1C24] hover:bg-red-50 hover:text-red-500 hover:border-red-200 transition-all shadow-sm absolute right-[30px] top-[-12px]"
                                            title="Delete Slot"
                                          >
                                            <Trash2 size={13} strokeWidth={3} />
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <OfferListTable refreshKey={refreshKey} />
      <ErrorPopup error={error} onClose={() => setError(null)} />
    </>
  );
}
